import type {
  BridgeState,
  PersonaChoice,
  ThemeId,
  AspirationId,
  TimeBudgetId,
  ObstacleId,
} from "./types";
import {
  THEME_ORDER,
  ASPIRATION_ORDER,
  TIME_BUDGET_ORDER,
  OBSTACLE_ORDER,
} from "./types";
import {
  MAX_THEMES,
  MAX_ASPIRATIONS,
  MAX_OBSTACLES,
  MAX_WEEKLY_REFLECTIONS,
  FREE_TEXT_MAX,
  USER_INTERPRETATION_MAX,
  WEEKLY_REFLECTION_MAX,
} from "./options";

export const ENGINE_VERSION = "dtr-1.0";

/**
 * Empty/Initial BridgeState.
 */
export function emptyBridgeState(): BridgeState {
  return {
    engineVersion: ENGINE_VERSION,
    personaChoice: null,
    freeText: "",
    themes: [],
    aspirations: [],
    interpretationDecision: "pending",
    rewordedText: "",
    timeBudget: null,
    obstacles: [],
    progress: {
      planId: "",
      daysDone: [false, false, false, false, false, false, false],
      goalSteps: [
        [false, false, false],
        [false, false, false],
        [false, false, false],
      ],
    },
    weeklyReflections: [],
    includeInDossier: false,
  };
}

/**
 * Key generator for safe progress tracking tied to a specific planId.
 */
export function progressKeyFor(planId: string): string {
  return `ql.dreamBridge.progress.${planId}`;
}

/**
 * Clean and sanitize user free-text inputs:
 * - Trims edges
 * - Collapses internal control characters and duplicate whitespace
 * - Clamps to specified maximum length
 */
export function sanitizeFreeText(raw: unknown, max: number): string {
  if (typeof raw !== "string") return "";
  // Strip null bytes and non-printable control characters, but keep newlines/tabs
  const cleaned = raw.replace(/[\x00-\x08\x0B\x0C\x0E-\x1F\x7F]/g, "");
  const trimmed = cleaned.trim().replace(/[ \t]+/g, " ");
  if (trimmed.length > max) {
    return trimmed.slice(0, max);
  }
  return trimmed;
}

/**
 * Strict parser for BridgeState.
 * Validates every field against option lists and bounds.
 * Drops anything unknown, never throws, returns null if raw is structurally invalid.
 */
export function parseBridgeState(raw: unknown): BridgeState | null {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) {
    return null;
  }

  const obj = raw as Record<string, unknown>;

  // Check version
  if (obj.engineVersion !== ENGINE_VERSION) {
    return null;
  }

  // PersonaChoice
  let personaChoice: PersonaChoice | null = null;
  if (obj.personaChoice && typeof obj.personaChoice === "object") {
    const pc = obj.personaChoice as Record<string, unknown>;
    if (pc.kind === "own") {
      personaChoice = { kind: "own" };
    } else if (pc.kind === "self") {
      const idx = Number(pc.index);
      if (idx === 0 || idx === 1 || idx === 2 || idx === 3) {
        personaChoice = { kind: "self", index: idx as 0 | 1 | 2 | 3 };
      }
    }
  }

  // FreeText
  const freeText = sanitizeFreeText(obj.freeText, FREE_TEXT_MAX);

  // Themes
  const themes: ThemeId[] = [];
  if (Array.isArray(obj.themes)) {
    for (const t of obj.themes) {
      if (typeof t === "string" && THEME_ORDER.includes(t as ThemeId)) {
        if (!themes.includes(t as ThemeId) && themes.length < MAX_THEMES) {
          themes.push(t as ThemeId);
        }
      }
    }
  }

  // Aspirations
  const aspirations: AspirationId[] = [];
  if (Array.isArray(obj.aspirations)) {
    for (const a of obj.aspirations) {
      if (typeof a === "string" && ASPIRATION_ORDER.includes(a as AspirationId)) {
        if (!aspirations.includes(a as AspirationId) && aspirations.length < MAX_ASPIRATIONS) {
          aspirations.push(a as AspirationId);
        }
      }
    }
  }

  // Interpretation decision
  let interpretationDecision: BridgeState["interpretationDecision"] = "pending";
  if (
    obj.interpretationDecision === "accepted" ||
    obj.interpretationDecision === "reworded" ||
    obj.interpretationDecision === "rejected"
  ) {
    interpretationDecision = obj.interpretationDecision;
  }

  // Reworded text
  const rewordedText = sanitizeFreeText(obj.rewordedText, USER_INTERPRETATION_MAX);

  // Time budget
  let timeBudget: TimeBudgetId | null = null;
  if (typeof obj.timeBudget === "string" && TIME_BUDGET_ORDER.includes(obj.timeBudget as TimeBudgetId)) {
    timeBudget = obj.timeBudget as TimeBudgetId;
  }

  // Obstacles
  const obstacles: ObstacleId[] = [];
  if (Array.isArray(obj.obstacles)) {
    for (const o of obj.obstacles) {
      if (typeof o === "string" && OBSTACLE_ORDER.includes(o as ObstacleId)) {
        if (!obstacles.includes(o as ObstacleId) && obstacles.length < MAX_OBSTACLES) {
          obstacles.push(o as ObstacleId);
        }
      }
    }
  }

  // Progress
  let planId = "";
  let daysDone: [boolean, boolean, boolean, boolean, boolean, boolean, boolean] = [
    false,
    false,
    false,
    false,
    false,
    false,
    false,
  ];
  let goalSteps: [
    [boolean, boolean, boolean],
    [boolean, boolean, boolean],
    [boolean, boolean, boolean],
  ] = [
    [false, false, false],
    [false, false, false],
    [false, false, false],
  ];

  if (obj.progress && typeof obj.progress === "object") {
    const prog = obj.progress as Record<string, unknown>;
    if (typeof prog.planId === "string") {
      planId = prog.planId.slice(0, 32);
    }
    if (Array.isArray(prog.daysDone) && prog.daysDone.length === 7) {
      daysDone = [
        Boolean(prog.daysDone[0]),
        Boolean(prog.daysDone[1]),
        Boolean(prog.daysDone[2]),
        Boolean(prog.daysDone[3]),
        Boolean(prog.daysDone[4]),
        Boolean(prog.daysDone[5]),
        Boolean(prog.daysDone[6]),
      ];
    }
    if (Array.isArray(prog.goalSteps) && prog.goalSteps.length === 3) {
      goalSteps = [
        [
          Boolean((prog.goalSteps[0] as unknown[])?.[0]),
          Boolean((prog.goalSteps[0] as unknown[])?.[1]),
          Boolean((prog.goalSteps[0] as unknown[])?.[2]),
        ],
        [
          Boolean((prog.goalSteps[1] as unknown[])?.[0]),
          Boolean((prog.goalSteps[1] as unknown[])?.[1]),
          Boolean((prog.goalSteps[1] as unknown[])?.[2]),
        ],
        [
          Boolean((prog.goalSteps[2] as unknown[])?.[0]),
          Boolean((prog.goalSteps[2] as unknown[])?.[1]),
          Boolean((prog.goalSteps[2] as unknown[])?.[2]),
        ],
      ];
    }
  }

  // Weekly reflections
  const weeklyReflections: { weekLabel: string; text: string }[] = [];
  if (Array.isArray(obj.weeklyReflections)) {
    for (const item of obj.weeklyReflections) {
      if (item && typeof item === "object") {
        const entry = item as Record<string, unknown>;
        const label = sanitizeFreeText(entry.weekLabel, 60);
        const text = sanitizeFreeText(entry.text, WEEKLY_REFLECTION_MAX);
        if (label && text && weeklyReflections.length < MAX_WEEKLY_REFLECTIONS) {
          weeklyReflections.push({ weekLabel: label, text });
        }
      }
    }
  }

  // Include in dossier
  const includeInDossier = Boolean(obj.includeInDossier);

  return {
    engineVersion: ENGINE_VERSION,
    personaChoice,
    freeText,
    themes,
    aspirations,
    interpretationDecision,
    rewordedText,
    timeBudget,
    obstacles,
    progress: {
      planId,
      daysDone,
      goalSteps,
    },
    weeklyReflections,
    includeInDossier,
  };
}
